"use client";
import { AnimatePresence } from "framer-motion";
import Backdrop from "./backdrop";
import React, {
  CSSProperties,
  ReactElement,
  ReactHTMLElement,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { ModalContainer, ModalContent } from "../Modal/styles";
import { CloseButton } from "../buttons/CloseButton";
import styles from "./styles.module.scss";
import { usePathname, useRouter } from "next/navigation";
import { preventDefaultAndStopPropagation } from "@itchannelplus/utils";

const animations = {
  dropIn: {
    hidden: {
      y: "-100vh",
      opacity: 0,
    },
    visible: {
      y: "0",
      opacity: 1,
      transition: {
        duration: 0.1,
        type: "spring",
        damping: 25,
        stiffness: 500,
      },
    },
    exit: {
      y: "100vh",
      opacity: 0,
    },
  },
  fade: {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.1,
      },
    },
    exit: {
      opacity: 0,
    },
  },
  fromRight: {
    hidden: {
      x: "100vw",
      opacity: 0,
    },
    visible: {
      x: "0",
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
    exit: {
      x: "100vw",
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
  },
  fromBottom: {
    hidden: {
      y: "100vh",
      opacity: 1,
    },
    visible: {
      y: "0",
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
    exit: {
      y: "100vh",
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
  },
};

export interface ModalProps {
  onClose?(): void;
  backOnclose?: boolean;
  closeOnPathChange?: boolean;
  isOpened?: boolean;
  type?: "full" | "panel" | "popup";
  appear?: "dropIn" | "fromRight" | "fromBottom" | "fade";
  className?: string;
  backdropClassName?: string;
  triggerNode?: ReactElement<any>;
  style?: CSSProperties;
  theme?: "light" | "dark";
  showCloseButton?: boolean;
  backgroundLogo?: boolean;
  [key: string]: any;
}

const Modal = ({
  onClose,
  isOpened,
  closeOnPathChange,
  type = "full",
  appear = "fromBottom",
  backgroundLogo,
  className,
  backdropClassName,
  theme,
  showCloseButton = true,
  backOnclose = false,
  triggerNode,
  style,
  children,
}: ModalProps) => {
  const [open, setOpen] = useState(isOpened);
  const [initialPathname, setInitialPathname] = useState(usePathname());
  const router = useRouter();
  const pathname = usePathname();
  const toggleClass = (isOpen:boolean) => {
    if (typeof window != "undefined") {
      if (isOpen) window.document.body.classList.add("modal-opened");
      else window.document.body.classList.remove("modal-opened");
    }
  };
  //console.log("#### modal", name, isOpened);

  useEffect(() => {
    //automatically close the modal when we navigate to a new link
    //console.log("initialPathname", initialPathname, pathname);
    if (closeOnPathChange) {
      if (initialPathname !== pathname) {
        setOpen(false);
        toggleClass(false);
        if (onClose) onClose();
      } else {
        toggleClass(!!isOpened);
        setOpen(isOpened); //we restore initial state
      }
    }
  }, [initialPathname, pathname, closeOnPathChange, isOpened, onClose]);

  useEffect(() => {
    toggleClass(!!open);
  }, [open]);
  useEffect(() => {
    setOpen(isOpened);
  }, [isOpened]);

  const close = () => {
    setOpen(false);
    toggleClass(false);
    if (onClose) onClose();
    if (backOnclose) setTimeout(() => router.back(), 500);
  };
  return (
    <>
      {triggerNode &&
        React.cloneElement(triggerNode as ReactHTMLElement<any>, {
          onClick: () => setOpen(true),
        })}
      <AnimatePresence
        // Disable any initial animations on children that
        // are present when the component is first rendered
        initial={true}
        // Only render one component at a time.
        // The exiting component will finish its exit
        // animation before entering component is rendered
        mode="wait"
        // Fires when all exiting nodes have completed animating out
        onExitComplete={() => null}
      >
        {open && (
          <Backdrop
            onClick={close}
            key={"modal-backdrop"}
            className={backdropClassName}
            type={type}
            theme={theme}
          >
            <ModalContainer
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className={className}
              variants={animations[appear] as any}
              initial="hidden"
              animate="visible"
              exit="exit"
              type={type}
              appear={appear}
              style={style}
              theme={theme}
              key={"modal-container"}
              backgroundLogo={backgroundLogo}
            >
              <ModalContent>{children}</ModalContent>
              {showCloseButton && (
                <CloseButton
                  onClick={close}
                  className={styles.closeButton}
                  theme={theme == "light" ? "dark" : "light"}
                />
              )}
            </ModalContainer>
          </Backdrop>
        )}
      </AnimatePresence>
    </>
  );
};

interface ModalTriggerProps extends ModalProps {
  trigger: ReactNode;
}

export const ModalTrigger = ({
  isOpened,
  onClose,
  trigger,
  children,
  type,
  appear,
  className,
  backdropClassName,
  theme,
  showCloseButton,
  backgroundLogo,
  style,
}: ModalTriggerProps) => {
  const [showModal, setShowModal] = useState(isOpened || false);
  useEffect(() => {
    if (!showModal && onClose) {
      onClose();
    }
  }, [showModal, onClose]);
  return (
    <>
      <div
        className={"pointer"}
        onClick={preventDefaultAndStopPropagation(() => {
          setShowModal(true);
        })}
      >
        {trigger}
      </div>
      <Modal
        isOpened={showModal}
        onClose={() => setShowModal(false)}
        portal
        type={type}
        appear={appear}
        className={className}
        backdropClassName={backdropClassName}
        theme={theme}
        showCloseButton={showCloseButton}
        backgroundLogo={backgroundLogo}
        style={style}
      >
        {children}
      </Modal>
    </>
  );
};

export default Modal;
